import React, { useState } from "react";
import DOMPurify from "dompurify";
import axios from "axios";
import { X } from "lucide-react";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

const VARIABLE_LABELS = {
    "{{candidate_name}}":       "Candidate Name",
    "{{candidate_role}}":       "Role",
    "{{candidate_company}}":    "Company",
    "{{candidate_location}}":   "Location",
};

const applyVariablePreview = (html) => {
    if (!html) return html;
    return html.replace(/\{\{([^}]+)\}\}/g, (match, inner) => {
        const label = VARIABLE_LABELS[match]
            ?? inner.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        return `<em style="color:#7c3aed">${label}</em>`;
    });
};

const EmailTestModal = ({ isOpen, onClose, step, gmailEmail, orgId, recruiterId }) => {
    const [testEmail, setTestEmail] = useState("");
    const [toast, setToast] = useState(null); // { type: "success"|"error", msg: string } | null
    const [inputError, setInputError] = useState(false);
    const [sending, setSending] = useState(false);

    if (!isOpen || !step) return null;

    const senderDisplay = gmailEmail
        ? `${gmailEmail.split("@")[0]} <${gmailEmail}>`
        : "No email connected";

    const showToast = (type, msg) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };

    const handleSendTest = async () => {
        if (!isValidEmail(testEmail)) {
            showToast("error", "Please enter a valid email address");
            setInputError(true);
            return;
        }
        setInputError(false);
        setSending(true);
        try {
            await axios.post(`${SOURCING_URL}/email/send-test`, {
                to: testEmail,
                subject: step.subject || "No subject",
                body: applyVariablePreview(step.body || ""),
                org_id: orgId,
                created_by: recruiterId,
            });
            showToast("success", "Test email sent!");
            setTestEmail("");
        } catch (e) {
            showToast("error", e.response?.data?.detail || "Failed to send test email");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {toast && (
                <div className={`fixed top-5 right-5 z-[70] flex items-center gap-2 px-4 py-3 bg-white rounded-xl shadow-lg text-sm ${
                    toast.type === "success"
                        ? "border border-green-200 text-green-700"
                        : "border border-red-200 text-red-600"
                }`}>
                    {toast.type === "success" ? (
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
                        </svg>
                    )}
                    {toast.msg}
                </div>
            )}

            <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
                <div className="w-full max-w-2xl bg-white dark:bg-[#1B1524] rounded-2xl shadow-2xl flex flex-col h-[620px] overflow-hidden">

                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                        <h3 className="text-base font-semibold text-gray-800 dark:text-white">Email Preview</h3>
                        <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Middle — fixed rows + scrollable body */}
                    <div className="flex-1 flex flex-col min-h-0">

                        {/* From — fixed */}
                        <div className="flex items-center gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16 shrink-0">From:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{senderDisplay}</span>
                        </div>

                        {/* To — fixed */}
                        <div className="flex items-center gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16 shrink-0">To:</span>
                            <span className="text-sm italic text-gray-400 dark:text-gray-500">Candidate email</span>
                        </div>

                        {/* Subject — fixed */}
                        <div className="flex items-start gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-16 shrink-0 pt-0.5">Subject:</span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 flex-1">
                                {step.subject || <span className="text-gray-400 dark:text-gray-500 italic">No subject</span>}
                            </span>
                        </div>

                        {/* Body — fills remaining space, scrolls internally */}
                        <div className="flex-1 flex flex-col px-6 py-4 min-h-0">
                            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-xl p-4">
                                {step.body ? (
                                    <div
                                        className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(applyVariablePreview(step.body) || "") }}
                                    />
                                ) : (
                                    <span className="text-gray-400 dark:text-gray-500 italic text-sm">No content</span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer — send test email */}
                    <div className="border-t border-gray-100 dark:border-gray-700/50 px-6 py-4 shrink-0 bg-gray-50 dark:bg-white/[0.03] flex items-center gap-6">
                        <div className="shrink-0">
                            <p className="text-sm font-semibold text-gray-800 dark:text-white">Send yourself a test email</p>
                            <p className="text-xs text-blue-500 mt-0.5">Test emails have "[Test]" in the subject line</p>
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                            <div className="relative flex-1">
                                <input
                                    type="email"
                                    value={testEmail}
                                    onChange={e => { setTestEmail(e.target.value); }}
                                    placeholder="Enter email address"
                                    className={`w-full border rounded-xl px-4 py-2 text-sm outline-none focus:ring-1 transition-colors pr-8 ${
                                        inputError
                                            ? "border-red-300 focus:border-red-400 focus:ring-red-100"
                                            : "border-gray-200 focus:border-purple-400 focus:ring-purple-100"
                                    }`}
                                />
                                {testEmail && (
                                    <button
                                        onClick={() => { setTestEmail(""); setInputError(false); }}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                        tabIndex={-1}
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                            <button
                                disabled={sending || !testEmail.trim()}
                                onClick={handleSendTest}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium border shrink-0 transition-colors text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
                            >
                                {sending ? (
                                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <>
                                        Send Test
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default EmailTestModal;